import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import AppState from '@webapp/store/state';
import { AssetsState } from '../../store/assets/state';
import { UPDATE_TRACK } from '../../store/tracks/actions';
import SimpleTrack from '@common/types/release/simple-track';
import { UpdateTrack } from '../../store/tracks/action-creators';
import { ReleaseStatus } from '@common/types/release/relase-status';

import styled from 'styled-components';

import Box from '@components/layout/box';
import BoxHeader from '@components/layout/box-header';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import FormContainer from '@components/layout/form-container';
import StepButton from '@webapp/components/actions/step-button';
import TrackSelectorCtrl from '@webapp/controllers/release/track-selector';
import { ErrorMessage } from '@webapp/components/basics/error-message/index';
import UploadVideoCoverCtrl from '../../controllers/upload/upload-video-cover';
import UploadMusicVideoCtrl from '@webapp/controllers/upload/upload-music-video';
import { VideoRequirementsHint } from '../../components/upload/video-requirements-hint';
import { SimpleRelease } from '@common/types/release/simple-release';

interface MusicVideoPageProps {
  className?: string;

  assets: AssetsState['assets'];
  statuses: AssetsState['statuses'];
  isLoading: boolean;
  releases: SimpleRelease[];
  updateTrack: (track: SimpleTrack) => UpdateTrack;
}

const mapStateToProps = (state: AppState) => ({
  assets: state.assets.assets,
  statuses: state.assets.statuses,
  isLoading: !!state.ui.loading[UPDATE_TRACK],
  releases: state.release.list.published || []
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateTrack: (track: SimpleTrack) => dispatch(new UpdateTrack(track, true)),
});

export const MusicVideoPage = (props: MusicVideoPageProps) => {
  const handleNextStepClick = () => {
    if (!isValid()) {
      return false;
    }

    addNewVideo();

    return false;
  };

  const [videoError, setVideoError] = React.useState(false);
  const [coverError, setCoverError] = React.useState('');
  const [trackError, setTrackError] = React.useState(false);

  const [track, setSelection] = React.useState<SimpleTrack>();
  const [status, setStatus] = React.useState<ReleaseStatus>();

  const [trackingId, setTrackingId] = React.useState<string>();
  const [coverTrackingId, setCoverTrackingId] = React.useState<string>();

  const videoAsset = Object.values(props.assets).find(
    (a) => a.trackingId === trackingId
  );
  const videoStatus = videoAsset ? props.statuses[videoAsset.id] : undefined;
  const isValidating = videoStatus ? !videoStatus?.validationDone : false;
  const isVideoPending = videoAsset?.status === 'uploading' || isValidating;

  const isValid = () => {
    let valid = true;

    if (!(track && trackingId && coverTrackingId)) {
      valid = false;
    }

    setTrackError(!track);
    setVideoError(!trackingId);
    setCoverError(!coverTrackingId ? 'MUSIC_VIDEO.MISSING_COVER' : '');

    return valid;
  };

  const addNewVideo = () => {
    if (track && isValid()) {
      const video = Object.values(props.assets).find(
        (a) => a.trackingId === trackingId
      );
      const cover = Object.values(props.assets).find(
        (a) => a.trackingId === coverTrackingId
      );

      setVideoError(!video);
      setCoverError(!cover ? 'MUSIC_VIDEO.MISSING_COVER' : '');

      if (!(video && cover)) {
        return;
      }

      track.hasMusicVideo = true;
      track.video = {
        videoAssetId: video.id,
        videoAssetUrl: video.url,
        coverAssetId: cover.id,
        coverAssetUrl: cover.url,
      };

      props.updateTrack(track);
    }
  };

  const getLabel = () => {
    if (status === 'draft') {
      return 'Add Video';
    }

    if (isVideoPending) {
      if (isValidating) {
        return 'Validating...';
      }

      return 'Uploading...';
    }

    return 'Publish Video';
  };

  const hasPublishedReleases = (releases: SimpleRelease[]) => {
    return releases?.length >= 1;
  };

  return (
    <MotionOpacity>
      <FormContainer className={props.className}>
        <Typography className="title mb-40" weight="400" type="subheader">
          Upload Music Video
        </Typography>

        <VideoRequirementsHint className="mb-40" />

        {!hasPublishedReleases(props.releases) ? (
          <ErrorMessage
            className="mb-10"
            errorId="MUSIC_VIDEO.NO_PUBLISHED_RELEASES"
          />
        ) : null}

        <ErrorMessage
          className="mb-10"
          errorId={videoError ? 'MUSIC_VIDEO.MISSING_VIDEO' : undefined}
        />
        <UploadMusicVideoCtrl
          className="mb-40"
          trackingId={trackingId}
          onUpload={(newTrackingId) => {
            setTrackingId(newTrackingId);
            setVideoError(false);
          }}
          error={!!videoError}
        />

        <ErrorMessage
          className="mb-10"
          errorId={coverError ? coverError : undefined}
        />
        <UploadVideoCoverCtrl
          className="mb-40"
          trackingId={coverTrackingId}
          onUpload={(newTrackingId) => {
            setCoverTrackingId(newTrackingId);
            setCoverError('');
          }}
          onDataUrl={() => null}
          error={!!coverError}
          onError={setCoverError}
        />

        <ErrorMessage
          className="mb-10"
          errorId={trackError ? 'MUSIC_VIDEO.MISSING_TRACK' : undefined}
        />
        <Box>
          <BoxHeader title="Your Releases" />
          <TrackSelectorCtrl
            onSelection={(trackId, releaseStatus) => {
              setSelection(trackId);
              setStatus(releaseStatus);
            }}
          />
        </Box>

        <StepButton
          label={getLabel()}
          isValid={videoStatus?.isValid && !!track}
          isLoading={props.isLoading || isVideoPending}
          onNext={() => handleNextStepClick()}
          disableLoader={true}
        />
      </FormContainer>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(MusicVideoPage)`
  margin-top: 30px;
  padding-bottom: 200px;

  @media ${({ theme }) => theme.device.tablet} {
    margin-top: 70px;
  }
`);
